import { cn, formatWilson, getBandBg } from '@/lib/utils'
import type { ConfidenceBreakdown } from '@/lib/types'

interface ConfidenceMeterProps {
  confidence: ConfidenceBreakdown
  groundTruthPresent?: boolean
  showDrivers?: boolean
  className?: string
}

export function ConfidenceMeter({ confidence, groundTruthPresent = false, showDrivers = false, className }: ConfidenceMeterProps) {
  const { band, wilson, drivers, weak_signals, score } = confidence
  const pct = Math.round(score * 100)

  const label = groundTruthPresent ? `Confidence: ${band}` : `Expected confidence: ${band}`
  const wilsonLabel = wilson?.length === 2
    ? (groundTruthPresent ? formatWilson(wilson) : `${formatWilson(wilson)} (proxy)`)
    : '—'

  const driverLabels: Record<string, string> = {
    evidence_volume: 'Evidence volume',
    evidence_relevance: 'Evidence relevance',
    segment_coverage: 'Segment coverage',
    open_ended_depth: 'Open-ended depth',
    pass_agreement: 'Pass agreement',
    recency: 'Recency',
    validation_history: 'Validation history',
    source_diversity: 'Source diversity',
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center justify-between">
        <span className={cn('text-sm font-medium px-2 py-0.5 rounded-full border', getBandBg(band))}>
          {label}
        </span>
        <span className="text-xs text-slate-500">{wilsonLabel}</span>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-soft-panel rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            band === 'High' ? 'bg-validation-green' : band === 'Medium' ? 'bg-uncertainty-amber' : 'bg-risk-rose'
          )}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Weak signals */}
      {weak_signals.length > 0 && (
        <div className="space-y-1">
          {weak_signals.map((signal, i) => (
            <p key={i} className="text-xs text-uncertainty-amber flex items-start gap-1.5">
              <span className="mt-0.5 shrink-0">⚠</span>
              {signal}
            </p>
          ))}
        </div>
      )}

      {/* Drivers breakdown */}
      {showDrivers && (
        <div className="pt-2 border-t border-border-gray space-y-1.5">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Confidence drivers</p>
          {Object.entries(driverLabels).map(([key, label]) => {
            const val = drivers[key as keyof typeof drivers] as number
            return (
              <div key={key} className="flex items-center gap-2">
                <span className="text-xs text-slate-600 w-36 shrink-0">{label}</span>
                <div className="flex-1 h-1.5 bg-soft-panel rounded-full">
                  <div
                    className="h-full bg-human-teal rounded-full"
                    style={{ width: `${Math.round(val * 100)}%` }}
                  />
                </div>
                <span className="text-xs text-slate-500 w-8 text-right">{Math.round(val * 100)}%</span>
              </div>
            )
          })}
          <p className="text-xs text-slate-400 italic mt-2">
            Note: Diversity signals are shown separately — they inform the validation plan, not the confidence score.
          </p>
        </div>
      )}
    </div>
  )
}
